import { type Ref, useState } from 'react';

import { LandingButton } from './LandingButton';
import * as styles from './ReferralHero.css';
import {
  GENERIC_HEADLINE,
  GENERIC_SUBTITLE,
  INVALID_SUBTITLE,
  OFFER_SUBTITLE,
  type ReferralOffer,
  buildCtaLabel,
  buildReferralHeadline,
} from './referral-copy';

interface ReferrerAvatarProps {
  firstName: string | null;
  imageUrl: string | null;
  loading: boolean;
}

const ReferrerAvatar = ({ firstName, imageUrl, loading }: ReferrerAvatarProps) => {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(imageUrl) && !imageFailed;
  const state = showImage ? 'image' : loading ? 'loading' : firstName ? 'initial' : 'none';

  if (state === 'none') {
    return null;
  }

  return (
    <div className={styles.avatar} data-testid="referrer-avatar" data-state={state}>
      {state === 'initial' ? (
        <span className={styles.avatarFallback} aria-hidden>
          {firstName?.charAt(0).toUpperCase()}
        </span>
      ) : (
        <span className={styles.skeleton} aria-hidden />
      )}
      {showImage && imageUrl ? (
        <img
          className={styles.avatarImage}
          src={imageUrl}
          alt=""
          fetchPriority="high"
          decoding="async"
          onError={() => setImageFailed(true)}
        />
      ) : null}
    </div>
  );
};

interface ReferralHeroProps {
  /** Receives the claim button's wrapper so the sticky bar can take over once it scrolls away. */
  ctaRef?: Ref<HTMLDivElement>;
  offer: ReferralOffer;
  firstName: string | null;
  imageUrl: string | null;
  discountPercent: number;
  claimHref: string;
  onClaimClick?: () => void;
}

function subtitleFor(offer: ReferralOffer): string {
  switch (offer) {
    case 'valid':
    case 'loading':
      return OFFER_SUBTITLE;
    case 'invalid':
      return INVALID_SUBTITLE;
    default:
      return GENERIC_SUBTITLE;
  }
}

export const ReferralHero = ({
  ctaRef,
  offer,
  firstName,
  imageUrl,
  discountPercent,
  claimHref,
  onClaimClick,
}: ReferralHeroProps) => {
  const headline =
    offer === 'valid' ? buildReferralHeadline(firstName, discountPercent) : GENERIC_HEADLINE;

  return (
    <section className={styles.hero} data-offer={offer}>
      {offer === 'valid' || offer === 'loading' ? (
        <ReferrerAvatar
          firstName={offer === 'valid' ? firstName : null}
          imageUrl={offer === 'valid' ? imageUrl : null}
          loading={offer === 'loading'}
        />
      ) : null}
      {offer === 'loading' ? (
        <h1 className={styles.heading} aria-busy="true" aria-label="Loading your referral">
          <span className={styles.headingSkeleton} aria-hidden>
            <span className={`${styles.skeleton} ${styles.headingSkeletonLine}`} />
            <span className={`${styles.skeleton} ${styles.headingSkeletonLineShort}`} />
          </span>
        </h1>
      ) : (
        <h1 className={styles.heading}>
          <span key={headline} className={styles.headingText}>
            {headline}
          </span>
        </h1>
      )}
      <p className={styles.subtitle}>{subtitleFor(offer)}</p>
      <div ref={ctaRef} className={styles.cta}>
        <LandingButton href={claimHref} onClick={onClaimClick}>
          {buildCtaLabel(offer, discountPercent)}
        </LandingButton>
      </div>
    </section>
  );
};
